import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { ReviewPageActions } from '../../actions';
import { Observable, Subject, takeUntil } from 'rxjs';
import {
  selectCanRelease,
  selectIsBundled,
  selectIsUseStandingAuth,
  selectSaveAsTemplateToggledOn,
  selectShowSubmitAndCreate,
  selectSubmitAndCreateToggledOn,
  selectTransactionType,
} from '../../reducers';
import { TransactionType } from '../../../shared/types/Transaction';
import { transactionTypes } from '../../../shared/utilities/Constants';
import { selectCreateWireTemplateCallSuccessful } from '../../../account-info-header/reducers';
import { mapInitiatedDraftsToTableColumn } from '../transactions-table/table-helpers';
import { selectIsCreateSloaToggleFlagEnabled, selectIsEnableWiresCondensedLayout } from '../../../feature-flags/reducers';
import { selectInitReleaseTransactionInContext } from '../../../initiate-and-release/reducers';
import { selectHasAccessInitiateMoneyMovement } from '../../../authorize/reducers';
import { DraftListTableColumn } from '../../../initiate-and-release/Models/Drafts/DraftResponse';

@Component({
  standalone: false,
  selector: 'mm-options',
  templateUrl: './options.component.html',
  styleUrls: ['./options.component.scss'],
})
export class OptionsComponent implements OnInit, OnDestroy {
  @Input()
  isClientAuthShown: boolean = true;
  @Output() isSubmitAndCreate: EventEmitter<boolean> = new EventEmitter();
  showSubmitAndCreate$: Observable<boolean>;

  submitAndCreateToggledOn$: Observable<boolean>;
  saveAsTemplateToggledOn$: Observable<boolean>;
  isUseStandingAuth: boolean;
  transactionType: TransactionType | '';
  isBundledFlow: boolean = false;
  saveAsTemplateCallSucceeded: boolean;

  destroy$: Subject<boolean> = new Subject();
  draftInContext: DraftListTableColumn | null;
  hasMmInitiateAccess: boolean = false;
  customRuleCreateSloaToggleEnabled: boolean;
  canRelease: boolean;
  useCondensed: boolean = false;

  constructor(private store: Store) {}

  ngOnInit(): void {
    this.showSubmitAndCreate$ = this.store.select(selectShowSubmitAndCreate);

    this.submitAndCreateToggledOn$ = this.store.select(
      selectSubmitAndCreateToggledOn
    );
    this.saveAsTemplateToggledOn$ = this.store.select(
      selectSaveAsTemplateToggledOn
    );

    this.store
      .select(selectTransactionType)
      .pipe(takeUntil(this.destroy$))
      .subscribe((transactionType) => {
        this.transactionType = transactionType;
      });

    this.store
      .select(selectIsEnableWiresCondensedLayout)
      .pipe(takeUntil(this.destroy$))
      .subscribe((flag) => {
        this.useCondensed =
          flag && this.transactionType === transactionTypes.wire;
      });

    this.store
      .select(selectIsUseStandingAuth)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isUseStandingAuth) => {
        this.isUseStandingAuth = isUseStandingAuth;
      });

    this.store.select(selectIsBundled)
      .pipe(takeUntil(this.destroy$))
      .subscribe(useBundled => {
        this.isBundledFlow = useBundled;
    });

    this.store
      .select(selectCreateWireTemplateCallSuccessful)
      .pipe(takeUntil(this.destroy$))
      .subscribe((createTemplateSucceeded) => {
        this.saveAsTemplateCallSucceeded = createTemplateSucceeded;
      });

    this.store.select(selectInitReleaseTransactionInContext).pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.draftInContext = value ? mapInitiatedDraftsToTableColumn(value) : null;
    });

    this.store.select(selectHasAccessInitiateMoneyMovement).pipe(takeUntil(this.destroy$)).subscribe(hasMmInitiateAccess => {
      this.hasMmInitiateAccess = hasMmInitiateAccess;
    });

    this.store.select(selectIsCreateSloaToggleFlagEnabled).pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.customRuleCreateSloaToggleEnabled = val;
    });

    this.store.select(selectCanRelease).pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.canRelease = val;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  submitAndCreateToggled(toggled: boolean) {
    this.store.dispatch(
      ReviewPageActions.submitAndCreateToggled({ isSubmitAndCreate: toggled })
    );
  }

  saveAsTemplateToggled(toggled: boolean) {
    this.store.dispatch(ReviewPageActions.saveAsTemplateToggled({isSaveAsTemplate: toggled}));
  }

  get showTemplatesOption(): boolean {
    return (
      this.transactionType === transactionTypes.wire &&
      !this.isUseStandingAuth && !this.isBundledFlow && !this.saveAsTemplateCallSucceeded
    );
  }

  get DraftId(): boolean {
    if (this.draftInContext?.DraftId) {
      return true;
    }
    return false;
  }

  get isInitiateAndReleaseFlowFromTable(): boolean {
    return this.hasMmInitiateAccess && this.DraftId;
  }

  get showToggles(): boolean {
    if (!this.hasMmInitiateAccess) return true;
    if (this.hasMmInitiateAccess && this.customRuleCreateSloaToggleEnabled && !this.isInitiateAndReleaseFlowFromTable) { // Show toggle when custom rules and new SLOA toggle flag are on only when inititaing a transaction
      return true;
    }
    if (this.hasMmInitiateAccess && !this.customRuleCreateSloaToggleEnabled && this.isInitiateAndReleaseFlowFromTable && this.canRelease) { //Show toggle when custom rules flag is on and new SLOA toggle flag is false only when releasing a transaction
      return true;
    }
    return false;
  }
}
